<!-- Modal Header -->
<div class="modal-header">
    <h5 class="modal-title">
        <i class="ti ti-brain me-2"></i>
        Estratégia de Negociação com IA
    </h5>
    <button type="button" class="btn-close" (click)="close()"></button>
</div>

<!-- Modal Body -->
<div class="modal-body">
    @if (isLoading()) {
        <div class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Carregando...</span>
            </div>
            <p class="text-muted mt-3">Analisando dados e gerando estratégia...</p>
        </div>
    } @else if (strategy()) {
        <!-- Priority & Risk Score Banner -->
        <div class="row mb-4">
            <div class="col-md-6">
                <div class="info-card priority-card">
                    <div class="info-card-icon">
                        <i class="ti ti-flag-3"></i>
                    </div>
                    <div class="info-card-content">
                        <label class="info-card-label">Prioridade</label>
                        <div class="d-flex align-items-center gap-2">
                            <span class="badge" [class]="'bg-' + getPriorityColor(strategy().priority)">
                                {{ getPriorityLabel(strategy().priority) }}
                            </span>
                            <span class="priority-score">{{ strategy().priority }}/5</span>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-md-6">
                <div class="info-card risk-card">
                    <div class="info-card-icon">
                        <i class="ti ti-shield-check"></i>
                    </div>
                    <div class="info-card-content">
                        <label class="info-card-label">Risco</label>
                        <div class="d-flex align-items-center gap-2">
                            <span class="badge" [class]="'bg-' + getRiskColor(strategy().riskScore)">
                                {{ getRiskLabel(strategy().riskScore) }}
                            </span>
                            <span class="risk-score">{{ (strategy().riskScore * 100) | number:'1.0-0' }}%</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Transaction Eligibility (if eligible) -->
        @if (strategy().transactionEligibility && strategy().transactionEligibility.isEligible) {
            <div class="eligibility-alert mb-4">
                <div class="eligibility-header">
                    <i class="ti ti-checkbox"></i>
                    <div>
                        <h6 class="mb-1">Empresa Elegível para Programas de Transação</h6>
                        <p class="mb-0">Esta empresa pode aderir aos seguintes programas da PGFN:</p>
                    </div>
                </div>

                <div class="eligible-programs">
                    @for (program of strategy().transactionEligibility.eligiblePrograms; track $index) {
                        <span class="program-badge">
                            <i class="ti ti-check-circle me-1"></i>
                            {{ program }}
                        </span>
                    }
                </div>

                <div class="row g-3 mt-2">
                    <div class="col-md-4">
                        <div class="eligibility-metric">
                            <label>Desconto Máximo por Lei</label>
                            <strong>{{ (strategy().transactionEligibility.maxDiscountByLaw * 100) | number:'1.0-0' }}%</strong>
                        </div>
                    </div>
                    <div class="col-md-4">
                        <div class="eligibility-metric">
                            <label>Economia Estimada</label>
                            <strong class="text-success">{{ strategy().transactionEligibility.estimatedSavings | currency }}</strong>
                        </div>
                    </div>
                    <div class="col-md-4">
                        <div class="eligibility-metric">
                            <label>Base Legal</label>
                            <strong class="text-truncate d-block">{{ strategy().transactionEligibility.legalBasis }}</strong>
                        </div>
                    </div>
                </div>

                @if (strategy().transactionEligibility.conditions && strategy().transactionEligibility.conditions.length > 0) {
                    <div class="conditions-section mt-3">
                        <label class="conditions-label">Condições do Programa:</label>
                        <ul class="conditions-list">
                            @for (condition of strategy().transactionEligibility.conditions; track $index) {
                                <li>{{ condition }}</li>
                            }
                        </ul>
                    </div>
                }

                @if (strategy().transactionEligibility.requiredDocumentation && strategy().transactionEligibility.requiredDocumentation.length > 0) {
                    <div class="documentation-section mt-3">
                        <label class="documentation-label">Documentação Necessária:</label>
                        <div class="documentation-items">
                            @for (doc of strategy().transactionEligibility.requiredDocumentation; track $index) {
                                <span class="doc-item">
                                    <i class="ti ti-file-text me-1"></i>
                                    {{ doc }}
                                </span>
                            }
                        </div>
                    </div>
                }
            </div>
        }

        <!-- Recommended Action -->
        <div class="strategy-section mb-4">
            <div class="section-header">
                <i class="ti ti-bulb text-warning me-2"></i>
                <h6 class="mb-0">Ação Recomendada</h6>
            </div>
            <div class="recommended-action">
                {{ strategy().recommendedAction }}
            </div>
        </div>

        <!-- Negotiation Parameters -->
        <div class="strategy-section mb-4">
            <div class="section-header">
                <i class="ti ti-calculator text-primary me-2"></i>
                <h6 class="mb-0">Parâmetros de Negociação</h6>
            </div>
            <div class="row g-3">
                <div class="col-md-4">
                    <div class="param-card">
                        <div class="param-icon">
                            <i class="ti ti-discount"></i>
                        </div>
                        <div class="param-content">
                            <label>Desconto Máximo</label>
                            <strong>{{ (strategy().maxDiscount * 100) | number:'1.0-0' }}%</strong>
                        </div>
                    </div>
                </div>
                <div class="col-md-4">
                    <div class="param-card">
                        <div class="param-icon">
                            <i class="ti ti-calendar-stats"></i>
                        </div>
                        <div class="param-content">
                            <label>Parcelas Sugeridas</label>
                            <strong>{{ strategy().suggestedInstallments }}x</strong>
                        </div>
                    </div>
                </div>
                <div class="col-md-4">
                    <div class="param-card">
                        <div class="param-icon">
                            <i class="ti ti-clock"></i>
                        </div>
                        <div class="param-content">
                            <label>Prazo para Pagamento</label>
                            <strong>{{ strategy().paymentDeadline }}</strong>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Reasoning -->
        <div class="strategy-section mb-4">
            <div class="section-header">
                <i class="ti ti-chart-dots me-2"></i>
                <h6 class="mb-0">Análise</h6>
            </div>
            <div class="reasoning-box">
                <p class="mb-0">{{ strategy().reasoning }}</p>
            </div>
        </div>

        <!-- Key Insights -->
        <div class="strategy-section mb-4">
            <div class="section-header">
                <i class="ti ti-sparkles me-2"></i>
                <h6 class="mb-0">Insights Principais</h6>
            </div>
            <ul class="insights-list">
                @for (insight of strategy().keyInsights; track $index) {
                    <li>
                        <i class="ti ti-check me-2"></i>
                        {{ insight }}
                    </li>
                }
            </ul>
        </div>

        <!-- Negotiation Tips -->
        <div class="strategy-section mb-4">
            <div class="section-header">
                <i class="ti ti-lightbulb me-2"></i>
                <h6 class="mb-0">Dicas de Negociação</h6>
            </div>
            <ul class="tips-list">
                @for (tip of strategy().negotiationTips; track $index) {
                    <li>
                        <i class="ti ti-point-filled me-2"></i>
                        {{ tip }}
                    </li>
                }
            </ul>
        </div>

        <!-- Summary -->
        @if (strategy().summary) {
            <div class="strategy-section">
                <div class="section-header">
                    <i class="ti ti-file-analytics me-2"></i>
                    <h6 class="mb-0">Resumo da Situação</h6>
                </div>
                <div class="summary-grid">
                    <div class="summary-item">
                        <label>Total de Débitos</label>
                        <span>{{ strategy().summary.totalDebts }}</span>
                    </div>
                    <div class="summary-item">
                        <label>Valor Total</label>
                        <span>{{ strategy().summary.totalAmount | currency }}</span>
                    </div>
                    <div class="summary-item">
                        <label>Idade Média das Dívidas</label>
                        <span>{{ strategy().summary.averageDebtAge }} dias</span>
                    </div>
                    <div class="summary-item">
                        <label>Débitos em Atraso</label>
                        <span [class.text-danger]="strategy().summary.hasOverdueDebts">
                            {{ strategy().summary.hasOverdueDebts ? 'Sim' : 'Não' }}
                        </span>
                    </div>
                    <div class="summary-item">
                        <label>% em Atraso</label>
                        <span>{{ (strategy().summary.overduePercentage * 100) | number:'1.0-0' }}%</span>
                    </div>
                    <div class="summary-item">
                        <label>Perfil da Empresa</label>
                        <span class="company-profile">{{ strategy().summary.companyProfile }}</span>
                    </div>
                    <div class="summary-item full-width">
                        <label>Histórico de Negociações</label>
                        <span>{{ strategy().summary.negotiationHistory }}</span>
                    </div>
                </div>
            </div>
        }
    } @else {
        <div class="alert alert-warning">
            <i class="ti ti-alert-triangle me-2"></i>
            Não foi possível carregar a estratégia de negociação.
        </div>
    }
</div>

<!-- Modal Footer -->
<div class="modal-footer">
    <button type="button" class="btn btn-secondary" (click)="close()">Fechar</button>
</div>

